import {CursorType} from '@/enums/diagrams';
import {MarkerCursorData} from '../cursorDiagramState';
import {CursorManager} from './cursorManager';
import {MarkerCursorLabelModel} from './labelModels/markerCursorLabelModel';
import {SimpleCursor} from './simpleCursor';
import {groupBy} from 'lodash';
import {MarkerSeriesPoint} from '@/interfaces/markers';
import {uuidv4} from '@u/uuidUtils';
import {CursorPlotLine} from './cursorPlotLine';

/*
  Класс курсора-маркера
*/
export class MarkerCursor extends SimpleCursor {
  // Тип курсора
  type: CursorType = CursorType.MarkerCursor;

  // Тип модели текстового представления
  labelModel: MarkerCursorLabelModel | null;

  // Ширина полосы
  protected bandWidth: number | null = null;

  // Линия маркера
  protected firstCursorPlotLine!: CursorPlotLine;

  // Точка маркера
  protected firstPoint: any;
  protected firstCopiedPoint: {x: number; y: number} | null = null;

  // Диапазон
  protected firstPlotBand: any;

  // Допустимые направления для выбора диапазона
  protected directions: string[];

  // Идентификаторы серии с маркерами
  protected firstMarkerSeries: MarkerSeriesPoint[] = [];

  // Отметка точки маркера
  private firstTooltips: any[] = [];

  // Точность представления нецелых чисел
  protected viewRoundingAccuracy = 2;

  protected firstColor = '#FF0000FF'; //'rgba(255, 0, 0, 1)';

  constructor(
    labelModel: MarkerCursorLabelModel | null = null,
    applyCallback: Function | null = null,
    color = '#00FF00FF',
    directions = ['left', 'right'],
  ) {
    super(labelModel, applyCallback, color);
    this.labelModel = labelModel;
    this.directions = directions;

    this.firstPlotBand = {
      id: `markerCursorFirstPlotBand-${this.id}`,
      color: 'rgba(109, 109, 109, 0.75)',
      from: undefined,
      to: undefined,
      zIndex: 0,
    };
  }

  /*
    Инициализация курсора
  */
  init(
    cursorManager: CursorManager,
    chart: any,
    bindedSeriesIds: string[] = [],
    isShowLine = true,
    isShowTooltips = false,
    cursorData: MarkerCursorData | null | undefined,
  ) {
    this.firstCursorPlotLine = new CursorPlotLine(chart, {
      id: `markerCursorFirstPlotLine-${this.id}`,
      value: undefined,
      width: 3,
      color: 'red',
      zIndex: 9998,
    });

    super.init(cursorManager, chart, bindedSeriesIds, isShowLine, isShowTooltips, cursorData);
  }

  /*
    Инициализация данных курсора
  */
  protected initCursorData(cursorData: MarkerCursorData | null | undefined) {
    if (!cursorData) return;
    super.initCursorData(cursorData);

    const firstPointX = cursorData.firstPointX;
    if (firstPointX) {
      const firstPoint = this.getIntersectionPoint(firstPointX, this.bindedSeries[0]);
      if (firstPoint) {
        this.firstCopiedPoint = {x: firstPoint.x, y: firstPoint.y};
      } else {
        const y = this.firstCopiedPoint ? this.firstCopiedPoint.y : 0;
        this.firstCopiedPoint = {x: firstPointX, y: y};
      }
    } else {
      this.firstCopiedPoint = null;
    }

    //TODO: вынести этот расчет отсюда в перерисовку
    if (this.copiedPoint && this.firstCopiedPoint)
      this.bandWidth = Math.abs(this.firstCopiedPoint.x - this.copiedPoint.x);
    else this.bandWidth = null;

    this.redrawPlotLinesBandsAndMarkers();
  }

  getCursorData() {
    return new MarkerCursorData(this.id, this.type, this.copiedPoint?.x, this.firstCopiedPoint?.x);
  }

  /*
    Обработчик клика по пустой области графика
  */
  protected onClickHandler() {
    if (this.isFreezeEvents) return;
    super.onClickHandler();
  }

  /*
    Обработчик клика по линии графика (серии) к которой привязан курсор
  */
  protected onSeriesClickHandler() {
    if (this.isFreezeEvents) return;
    super.onSeriesClickHandler();
  }

  protected onMoveHandler(e: any) {
    if (this.firstCursorPlotLine.isDrag()) {
      //сохраняем X координату точки маркера до изменения, чтобы потом кооректно посчитать направление dir
      const firstCopiedPointSavedX = this.firstCopiedPoint?.x;
      const newXValue = this.firstCursorPlotLine.translateX(e.pageX, e.chartX);
      if (!newXValue) return;

      this.bindedSeries.forEach((series: any) => {
        const intersectionPoint = this.getIntersectionPoint(newXValue, series);
        if (intersectionPoint) {
          this.firstMarkerSeries.forEach((ms: any) => {
            if (ms.chartSeriesId === series.options.id) {
              ms.x = intersectionPoint.x;
              ms.y = intersectionPoint.y;
              this.drawMarkersFirstLine(ms.id, ms.yAxis, {x: ms.x, y: ms.y});
            }
          });
          this.firstCopiedPoint = {x: intersectionPoint.x, y: intersectionPoint.y};
        }
      });

      if (this.firstCopiedPoint && this.copiedPoint && this.bandWidth) {
        const dir = firstCopiedPointSavedX && firstCopiedPointSavedX > this.copiedPoint.x ? -1 : 1;
        super.apply({x: this.firstCopiedPoint.x + dir * this.bandWidth, y: 0});
        this.drawPlotBand(this.firstCopiedPoint.x, this.copiedPoint.x, this.firstPlotBand);
      }

      this.notify();
    }

    if (this.cursorPlotLine.isDrag()) {
      const newXValue = this.cursorPlotLine.getTranslatedXValue(e.pageX);
      if (!newXValue || !this.checkForDirection(this.firstCopiedPoint, {x: newXValue})) return;
    }

    // Из-за notify вызываем в конце
    super.onMoveHandler(e);
    if (this.cursorPlotLine.isDrag()) {
      if (this.firstCopiedPoint && this.copiedPoint) {
        this.drawPlotBand(this.firstCopiedPoint.x, this.copiedPoint.x, this.firstPlotBand);
        this.bandWidth = Math.abs(this.firstCopiedPoint.x - this.copiedPoint.x);
      }
    }
  }

  protected onUpHandler() {
    super.onUpHandler();
    if (this.firstCursorPlotLine.isDrag()) this.firstCursorPlotLine.stopDrag();
  }

  /*
    Дейстивия при активации курсора
  */

  apply(point: any) {
    if (!this.firstCopiedPoint) {
      this.firstPoint = point;
      this.firstCopiedPoint = {x: point.x, y: point.y};
      this.redrawPlotLinesBandsAndMarkers();
    } else {
      if (!this.checkForDirection(this.firstCopiedPoint, point)) return;
      super.apply(point);
      this.drawPlotBand(this.firstCopiedPoint.x, point.x, this.firstPlotBand);
      this.bandWidth = Math.abs(this.firstCopiedPoint.x - point.x);
    }
  }

  /*
    Проверка на направление курсора
  */
  protected checkForDirection(firstPoint: any, point: any) {
    return (
      firstPoint.x === point.x ||
      (firstPoint.x < point.x && this.directions.includes('right')) ||
      (firstPoint.x > point.x && this.directions.includes('left'))
    );
  }

  /*
    Действия при очистке курсора
  */

  clear() {
    this.clearFirstTooltip();
    this.firstMarkerSeries.forEach((series: any) => {
      this.chart.get(series.id)?.remove();
    });
    this.removePlotBand(this.firstPlotBand);
    this.firstCursorPlotLine.remove();

    this.firstPoint = undefined;
    this.firstCopiedPoint = null;
    this.bandWidth = null;
    this.firstMarkerSeries = [];
    super.clear();
  }

  /*
   Рисуем серию с маркерами
 */

  protected drawMarkersFirstLine(markerSeriesId: string, axisNumber: number, point: any) {
    let markerSeries = this.chart.get(markerSeriesId);

    if (!markerSeries) {
      // если серия с маркерами не найдена, то создаем ее (без перерисовки)
      markerSeries = this.chart.addSeries(
        {
          id: markerSeriesId,
          showInLegend: false,
          type: 'scatter',
          data: [],
          lineWidth: 0,
          zIndex: 9999,
          marker: {
            symbol: 'circle',
            fillColor: this.firstColor,
            lineColor: this.firstColor,
            radius: 5,
            enabled: true,
            zIndex: 9999,
          },
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
            inactive: {
              enabled: false,
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          yAxis: axisNumber,
        },
        false,
      );
    }

    // добавляем точки маркеров в серию (с перерисовкой)
    markerSeries.setData([point]);
  }

  protected newFirstMarkerSeriesId(id: string) {
    return `firstMarkerSeries-${id}`;
  }

  /*
    Перерисовать все линии, диапазоны и маркеры
  */
  redrawPlotLinesBandsAndMarkers(redrawMarkersAndBands = true) {
    if (!this.firstCopiedPoint) {
      this.firstMarkerSeries.forEach((series: any) => {
        this.chart.get(series.id)?.remove();
      });
      this.removePlotBand(this.firstPlotBand);
      this.firstCursorPlotLine.remove();
      return;
    }

    const markerSeries: MarkerSeriesPoint[] = [];
    const extremes = this.chart.xAxis[0].getExtremes();

    if (extremes.min <= this.firstCopiedPoint.x && extremes.max >= this.firstCopiedPoint.x) {
      // для всех сравниваемых графиков находим точки пересечения с курсором и запоминаем их, вместе с номерами соответствующих осей
      this.bindedSeries.forEach((series: any) => {
        if (this.firstCopiedPoint?.x) {
          const s = this.chart.series.find((s: any) => s.userOptions.id == series.options.id);
          if (!s.visible) return;
          const intersectionPoint = this.getIntersectionPoint(this.firstCopiedPoint?.x, series);
          if (intersectionPoint)
            markerSeries.push({
              chartSeriesId: series.options.id,
              yAxis: series.options.yAxis,
              x: intersectionPoint.x,
              y: intersectionPoint.y,
            });
        }
      });
    }

    if (this.isShowLine && markerSeries.length)
      this.firstCursorPlotLine.redraw(this.firstCopiedPoint.x);
    else this.firstCursorPlotLine.remove();

    if (!redrawMarkersAndBands) return;

    //обновляем plotBand
    if (this.copiedPoint && this.firstCopiedPoint) {
      let minPointX = Math.min(this.copiedPoint.x, this.firstCopiedPoint.x);
      let maxPointX = Math.max(this.copiedPoint.x, this.firstCopiedPoint.x);
      minPointX = Math.max(minPointX, extremes.min);
      maxPointX = Math.min(maxPointX, extremes.max);
      this.drawPlotBand(minPointX, maxPointX, this.firstPlotBand);
    }

    this.firstMarkerSeries.forEach((series: any) => {
      const findIndex = markerSeries.findIndex(
        (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
      );
      if (findIndex === -1) this.chart.get(series.id)?.remove();
      else markerSeries[findIndex] = {...markerSeries[findIndex], id: series.id};
    });

    this.firstMarkerSeries = [...markerSeries];
    this.firstMarkerSeries.forEach((series: any) => {
      series.id = series.id !== undefined ? series.id : this.newFirstMarkerSeriesId(uuidv4());
      this.drawMarkersFirstLine(series.id, series.yAxis, {x: series.x, y: series.y});
    });
  }

  /*
    Действия при перерисовке (любом изменении графика)
  */
  render() {
    super.render();

    // если из сравнения был удален график, а с ним и ось, то нужно убрать соответствующую серию маркеров
    this.firstMarkerSeries = this.firstMarkerSeries.filter((series: any) => {
      return this.chart.yAxis[series.yAxis] !== undefined;
    });
    this.redrawFirstTooltip();
  }

  /*
    Перерисовка отметки маркера
  */
  private redrawFirstTooltip() {
    this.clearFirstTooltip();
    if (!this.isShowTooltips) return;

    this.firstMarkerSeries.forEach((series: any) => {
      const t = this.drawTooltip({x: series.x, y: series.y}, series.yAxis);
      this.tooltips.push(t);
    });
  }

  /*
    Очистить отметку (приходится дублировать метод базового класса, из-за того что при
      передаче отметки аргументом метод destroy портит ссылку на объект, и его уже не
      сделать undefined)
  */
  private clearFirstTooltip() {
    this.firstTooltips.forEach((t: any) => {
      t.destroy();
      t = undefined;
    });
    this.firstTooltips = [];
  }

  /*
    Переключение показа линии
  */
  showLine(show: boolean) {
    super.showLine(show);
    this.redrawPlotLinesBandsAndMarkers(false);
  }

  /*
    Переключение показа отметок
  */
  showTooltips(show: boolean) {
    super.showTooltips(show);
    this.redrawFirstTooltip();
  }

  /*
    Показатель того, что курсор применен
  */
  isApplied() {
    return this.firstCopiedPoint != null; //check null or undefined
  }

  /*
    Отрисовать plotBand в графике
  */
  protected drawPlotBand(from: number, to: number, plotBand: any) {
    this.removePlotBand(plotBand);
    plotBand.from = from;
    plotBand.to = to;
    this.chart.xAxis[0].addPlotBand(plotBand);
  }

  /*
    Удалить plotBand с графика
  */
  protected removePlotBand(plotBand: any) {
    if (this.findPlotLineOrBandInChart(plotBand.id))
      this.chart.xAxis[0].removePlotBand(plotBand.id);
  }

  /*
    Найти в графике plotLine или plotBand по id
  */
  protected findPlotLineOrBandInChart(id: string) {
    return this.chart.xAxis[0].plotLinesAndBands.find((e: any) => e.id === id);
  }

  /*
    Получение строки курсора
  */
  public getLabelStr() {
    if (!this.firstCopiedPoint) return '';

    let xValue = this.firstCopiedPoint.x.toFixed(this.viewRoundingAccuracy);
    if (this.labelModel && this.labelModel.xValueFormatter)
      xValue = this.labelModel.xValueFormatter(this.firstCopiedPoint.x);

    let yValuesStr = '';
    const groupedSeries = groupBy(this.firstMarkerSeries, 'yAxis');
    for (const yAxis in groupedSeries) {
      const yValues = groupedSeries[yAxis]
        .map((series: any) => (series.y ? series.y.toFixed(this.viewRoundingAccuracy) : ''))
        .join(', ');
      const axisNumber = groupedSeries[yAxis][0].yAxis;
      yValuesStr = `${yValuesStr}, ${this.chart.options.yAxis[axisNumber].title.text}:${yValues}`;
    }

    let labelStr = `${this.chart.options.xAxis[0].title.text}:${xValue}${yValuesStr}`;
    if (this.bandWidth !== null) {
      let bandValue = Math.abs(this.bandWidth).toFixed(this.viewRoundingAccuracy);
      if (this.labelModel && this.labelModel.xPlotBandValueFormatter)
        bandValue = this.labelModel.xPlotBandValueFormatter(bandValue);

      if (this.labelModel && this.labelModel.xUnits !== '' && this.labelModel.xUnits != '-')
        labelStr += `, Полоса, ${this.labelModel.xUnits}: ${bandValue}`;
      else if (this.labelModel && this.labelModel.xUnits == '-')
        labelStr += `, Полоса: ${bandValue}`;
      else labelStr += `, Полоса: ${bandValue}`;
    }
    return labelStr;
  }

  /*
    Установка заморозки событий
  */
  freezeEvents(freeze: boolean) {
    super.freezeEvents(freeze);
    this.firstCursorPlotLine.freezeEvents(freeze);
  }
}
